/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Credits Hub & Ledger Management View (Task 5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Coins,
  TrendingDown,
  Award,
  ShieldCheck,
  Search,
  Filter,
  Download,
  Copy,
  Check,
  RotateCw,
  SlidersHorizontal,
  Wallet,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { AdjustCreditsModal } from './AdjustCreditsModal';
import type {
  AdminCreditsOverview,
  AdminWorkspaceWalletItem,
  AdminLedgerItem,
  AdminCreditAdjustmentResponse,
} from '@sofia/shared-types';

export const AdminCreditsManagementView: React.FC = () => {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  // Navigation tab state
  const [activeTab, setActiveTab] = useState<'wallets' | 'ledger'>('wallets');

  // Wallets filter state
  const [walletSearch, setWalletSearch] = useState<string>('');
  const [walletStatusFilter, setWalletStatusFilter] = useState<string>('all');

  // Ledger filter state
  const [ledgerSearch, setLedgerSearch] = useState<string>('');
  const [ledgerTypeFilter, setLedgerTypeFilter] = useState<string>('all');

  // Modal & Notification state
  const [selectedWallet, setSelectedWallet] = useState<AdminWorkspaceWalletItem | null>(null);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState<boolean>(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // 1. Query System Credit Overview
  const {
    data: overviewData,
    refetch: refetchOverview,
  } = useQuery<{ success: boolean; data: AdminCreditsOverview }>({
    queryKey: ['admin-credits-overview'],
    queryFn: async () => {
      return adminClient.get('/api/v1/admin/credits/overview');
    },
    staleTime: 30000,
  });

  // 2. Query Workspace Wallets
  const {
    data: walletsData,
    isLoading: isWalletsLoading,
    refetch: refetchWallets,
  } = useQuery<{ success: boolean; data: { wallets: AdminWorkspaceWalletItem[]; total: number } }>({
    queryKey: ['admin-credits-wallets', walletSearch, walletStatusFilter],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (walletSearch) params.search = walletSearch;
      if (walletStatusFilter !== 'all') params.status = walletStatusFilter;
      return adminClient.get('/api/v1/admin/credits/wallets', { params });
    },
    staleTime: 30000,
  });

  // 3. Query Immutable Credit Ledger
  const {
    data: ledgerData,
    isLoading: isLedgerLoading,
    refetch: refetchLedger,
  } = useQuery<{ success: boolean; data: { entries: AdminLedgerItem[]; total: number } }>({
    queryKey: ['admin-credits-ledger', ledgerSearch, ledgerTypeFilter],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (ledgerSearch) params.search = ledgerSearch;
      if (ledgerTypeFilter !== 'all') params.type = ledgerTypeFilter;
      return adminClient.get('/api/v1/admin/credits/ledger', { params });
    },
    staleTime: 30000,
  });

  const overview = overviewData?.data || {
    totalCirculatingCredits: 1250000,
    totalCreditsUsedThisMonth: 185420,
    totalLifetimeAllocated: 3500000,
    totalWalletsCount: 12,
    totalAdjustmentsCount: 8,
    activeWorkspacesCount: 10,
  };

  const wallets = walletsData?.data?.wallets || [];
  const ledgerEntries = ledgerData?.data?.entries || [];

  const handleRefreshAll = async () => {
    await Promise.all([refetchOverview(), refetchWallets(), refetchLedger()]);
    showToast(t('admin.credits.toast.refreshSuccess'));
  };

  const handleOpenAdjust = (wallet: AdminWorkspaceWalletItem) => {
    setSelectedWallet(wallet);
    setIsAdjustModalOpen(true);
  };

  const handleAdjustSuccess = (res: AdminCreditAdjustmentResponse) => {
    void queryClient.invalidateQueries({ queryKey: ['admin-credits-overview'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-credits-wallets'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-credits-ledger'] });
    showToast(
      t('admin.credits.toast.adjustSuccess', {
        name: selectedWallet?.workspaceName || res.workspaceId,
      })
    );
  };

  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      showToast(t('admin.credits.ledger.copiedTx'));
    } catch {
      // Fallback
    }
  };

  const handleExportCsv = () => {
    const headers = [
      'Transaction ID',
      'Workspace ID',
      'Workspace Name',
      'User Email',
      'Amount',
      'Balance After',
      'Reason',
      'Reference ID',
      'Model Code',
      'Tokens Used',
      'Timestamp (ISO)',
    ];

    const rows = ledgerEntries.map((e) => [
      e.id,
      e.workspaceId,
      `"${e.workspaceName.replace(/"/g, '""')}"`,
      e.userEmail || '',
      e.amount.toString(),
      e.balanceAfter.toString(),
      `"${e.reason.replace(/"/g, '""')}"`,
      e.referenceId || '',
      e.modelCode || '',
      e.tokensUsed?.toString() || '',
      e.createdAt,
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `sophia-admin-credit-ledger-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Toast Notification Alert */}
      {toastMessage && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-xs font-medium animate-in fade-in slide-in-from-top-2 duration-200">
          <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-surface border border-surface-border shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="size-9 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
              <Coins className="size-5" />
            </div>
            <h1 className="text-xl font-bold font-heading text-foreground">
              {t('admin.credits.title')}
            </h1>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-primary/10 text-primary border border-primary/20">
              D1 Edge Ledger
            </span>
          </div>
          <p className="text-xs text-foreground-muted max-w-2xl">
            {t('admin.credits.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          <button
            type="button"
            onClick={handleRefreshAll}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-surface hover:bg-surface-elevated text-foreground border border-surface-border text-xs font-medium shadow-xs transition-colors"
          >
            <RotateCw className="size-3.5 text-foreground-muted" />
            <span>{t('admin.common.refresh' as any) || 'Refresh'}</span>
          </button>

          <button
            type="button"
            onClick={handleExportCsv}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-surface hover:bg-surface-elevated text-foreground border border-surface-border text-xs font-medium shadow-xs transition-colors"
          >
            <Download className="size-3.5 text-primary" />
            <span>{t('admin.credits.ledger.exportCsv')}</span>
          </button>
        </div>
      </div>

      {/* 4 Financial Liquidity KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Circulating Supply */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted">
              {t('admin.credits.kpi.circulating')}
            </span>
            <div className="size-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Coins className="size-4" />
            </div>
          </div>
          <div className="space-y-0.5">
            <div className="text-xl font-bold font-mono text-foreground">
              {formatNumber(overview.totalCirculatingCredits)}
            </div>
            <p className="text-[11px] text-foreground-muted truncate">
              {t('admin.credits.kpi.circulatingSub')}
            </p>
          </div>
        </div>

        {/* Card 2: Consumed This Month */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted">
              {t('admin.credits.kpi.consumedThisMonth')}
            </span>
            <div className="size-8 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <TrendingDown className="size-4" />
            </div>
          </div>
          <div className="space-y-0.5">
            <div className="text-xl font-bold font-mono text-foreground">
              {formatNumber(overview.totalCreditsUsedThisMonth)}
            </div>
            <p className="text-[11px] text-foreground-muted truncate">
              {t('admin.credits.kpi.consumedSub')}
            </p>
          </div>
        </div>

        {/* Card 3: Lifetime Allocated */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted">
              {t('admin.credits.kpi.lifetimeAllocated')}
            </span>
            <div className="size-8 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
              <Award className="size-4" />
            </div>
          </div>
          <div className="space-y-0.5">
            <div className="text-xl font-bold font-mono text-foreground">
              {formatNumber(overview.totalLifetimeAllocated)}
            </div>
            <p className="text-[11px] text-foreground-muted truncate">
              {t('admin.credits.kpi.lifetimeSub')}
            </p>
          </div>
        </div>

        {/* Card 4: Admin Interventions & Active Wallets */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted">
              {t('admin.credits.kpi.adjustments')}
            </span>
            <div className="size-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <ShieldCheck className="size-4" />
            </div>
          </div>
          <div className="space-y-0.5">
            <div className="text-xl font-bold font-mono text-foreground">
              {overview.totalAdjustmentsCount} <span className="text-xs text-foreground-muted font-normal font-sans">({overview.totalWalletsCount} wallets)</span>
            </div>
            <p className="text-[11px] text-foreground-muted truncate">
              {t('admin.credits.kpi.adjustmentsSub')}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation Pill Bar */}
      <div className="flex items-center gap-2 border-b border-surface-border pb-2">
        <button
          type="button"
          onClick={() => setActiveTab('wallets')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
            activeTab === 'wallets'
              ? 'bg-primary text-primary-fg shadow-xs'
              : 'text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
          }`}
        >
          <Wallet className="size-3.5" />
          <span>{t('admin.credits.tabs.wallets')}</span>
          <span
            className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
              activeTab === 'wallets'
                ? 'bg-white/20 text-white'
                : 'bg-muted text-foreground-muted'
            }`}
          >
            {wallets.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('ledger')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
            activeTab === 'ledger'
              ? 'bg-primary text-primary-fg shadow-xs'
              : 'text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
          }`}
        >
          <Clock className="size-3.5" />
          <span>{t('admin.credits.tabs.ledger')}</span>
          <span
            className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
              activeTab === 'ledger'
                ? 'bg-white/20 text-white'
                : 'bg-muted text-foreground-muted'
            }`}
          >
            {ledgerEntries.length}
          </span>
        </button>
      </div>

      {/* TAB 1: Workspace Wallets Directory */}
      {activeTab === 'wallets' && (
        <div className="space-y-4">
          {/* Filter and Search Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
              <input
                type="text"
                value={walletSearch}
                onChange={(e) => setWalletSearch(e.target.value)}
                placeholder={t('admin.credits.wallets.searchPlaceholder')}
                className="w-full pl-9 pr-4 py-2 rounded-xl bg-surface border border-surface-border text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
              />
            </div>

            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-surface border border-surface-border text-xs text-foreground">
              <Filter className="size-3.5 text-foreground-muted" />
              <select
                value={walletStatusFilter}
                onChange={(e) => setWalletStatusFilter(e.target.value)}
                className="bg-transparent border-none text-xs text-foreground focus:outline-none cursor-pointer"
              >
                <option value="all">{t('admin.credits.wallets.statusAll')}</option>
                <option value="healthy">{t('admin.credits.wallets.statusHealthy')}</option>
                <option value="warning">{t('admin.credits.wallets.statusLow')}</option>
                <option value="critical">{t('admin.credits.wallets.statusDepleted')}</option>
              </select>
            </div>
          </div>

          {/* Wallets Table */}
          <div className="overflow-x-auto rounded-2xl border border-surface-border bg-surface shadow-xs">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-surface-border bg-surface-elevated/40 text-[11px] font-semibold text-foreground-muted">
                  <th className="py-3 px-4">{t('admin.credits.wallets.colWorkspace')}</th>
                  <th className="py-3 px-4">{t('admin.credits.wallets.colOwner')}</th>
                  <th className="py-3 px-4">{t('admin.credits.wallets.colBalance')}</th>
                  <th className="py-3 px-4">{t('admin.credits.wallets.colLifetime')}</th>
                  <th className="py-3 px-4">{t('admin.credits.wallets.colUpdated')}</th>
                  <th className="py-3 px-4 text-right">{t('admin.credits.wallets.colActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {isWalletsLoading ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-foreground-muted">
                      <div className="flex items-center justify-center gap-2">
                        <RotateCw className="size-4 animate-spin text-primary" />
                        <span>Loading workspace wallets...</span>
                      </div>
                    </td>
                  </tr>
                ) : wallets.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-foreground-muted">
                      {t('admin.credits.wallets.empty')}
                    </td>
                  </tr>
                ) : (
                  wallets.map((wallet) => (
                    <tr
                      key={wallet.workspaceId}
                      className="hover:bg-surface-elevated/50 transition-colors group"
                    >
                      {/* Workspace Info */}
                      <td className="py-3 px-4">
                        <div className="space-y-0.5">
                          <div className="font-semibold text-foreground">
                            {wallet.workspaceName}
                          </div>
                          <div className="font-mono text-[10px] text-foreground-muted flex items-center gap-1.5">
                            <span>ID: {wallet.workspaceId}</span>
                            <span>•</span>
                            <span>slug: {wallet.slug}</span>
                          </div>
                        </div>
                      </td>

                      {/* Owner Email */}
                      <td className="py-3 px-4">
                        <div className="space-y-0.5">
                          <div className="text-foreground">{wallet.ownerName}</div>
                          <div className="text-[10px] text-foreground-muted font-mono truncate max-w-[180px]">
                            {wallet.ownerEmail}
                          </div>
                        </div>
                      </td>

                      {/* Balance & Health Badge */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-sm text-foreground">
                            {formatNumber(wallet.balance)}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ${
                              wallet.status === 'critical'
                                ? 'bg-rose-500/10 text-rose-500 border border-rose-500/20'
                                : wallet.status === 'warning'
                                  ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                                  : 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                            }`}
                          >
                            {wallet.status}
                          </span>
                        </div>
                      </td>

                      {/* Lifetime Credits */}
                      <td className="py-3 px-4 font-mono text-foreground-muted">
                        {formatNumber(wallet.lifetimeCredits)}
                      </td>

                      {/* Updated Date */}
                      <td className="py-3 px-4 text-foreground-muted text-[11px]">
                        {new Date(wallet.updatedAt * 1000).toLocaleDateString()}
                      </td>

                      {/* Adjust Actions */}
                      <td className="py-3 px-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleOpenAdjust(wallet)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated hover:bg-surface-elevated/80 text-foreground text-xs font-semibold shadow-2xs transition-all active:scale-95"
                        >
                          <SlidersHorizontal className="size-3 text-primary" />
                          <span>{t('admin.credits.wallets.btnAdjust')}</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: Immutable Credit Ledger Audit Log */}
      {activeTab === 'ledger' && (
        <div className="space-y-4">
          {/* Filter and Search Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
              <input
                type="text"
                value={ledgerSearch}
                onChange={(e) => setLedgerSearch(e.target.value)}
                placeholder={t('admin.credits.ledger.searchPlaceholder')}
                className="w-full pl-9 pr-4 py-2 rounded-xl bg-surface border border-surface-border text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
              />
            </div>

            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-surface border border-surface-border text-xs text-foreground">
              <Filter className="size-3.5 text-foreground-muted" />
              <select
                value={ledgerTypeFilter}
                onChange={(e) => setLedgerTypeFilter(e.target.value)}
                className="bg-transparent border-none text-xs text-foreground focus:outline-none cursor-pointer"
              >
                <option value="all">{t('admin.credits.ledger.typeAll')}</option>
                <option value="debit">{t('admin.credits.ledger.typeDebit')}</option>
                <option value="credit">{t('admin.credits.ledger.typeCredit')}</option>
                <option value="admin">{t('admin.credits.ledger.typeAdmin')}</option>
                <option value="refund">{t('admin.credits.ledger.typeRefund')}</option>
              </select>
            </div>
          </div>

          {/* Ledger Table */}
          <div className="overflow-x-auto rounded-2xl border border-surface-border bg-surface shadow-xs">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-surface-border bg-surface-elevated/40 text-[11px] font-semibold text-foreground-muted">
                  <th className="py-3 px-4">{t('admin.credits.ledger.colTxId')}</th>
                  <th className="py-3 px-4">{t('admin.credits.ledger.colWorkspace')}</th>
                  <th className="py-3 px-4">{t('admin.credits.ledger.colType')}</th>
                  <th className="py-3 px-4">{t('admin.credits.ledger.colAmount')}</th>
                  <th className="py-3 px-4">{t('admin.credits.ledger.colBalanceAfter')}</th>
                  <th className="py-3 px-4">{t('admin.credits.ledger.colReference')}</th>
                  <th className="py-3 px-4">{t('admin.credits.ledger.colTimestamp')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {isLedgerLoading ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-foreground-muted">
                      <div className="flex items-center justify-center gap-2">
                        <RotateCw className="size-4 animate-spin text-primary" />
                        <span>Loading immutable ledger...</span>
                      </div>
                    </td>
                  </tr>
                ) : ledgerEntries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-foreground-muted">
                      {t('admin.credits.ledger.empty')}
                    </td>
                  </tr>
                ) : (
                  ledgerEntries.map((entry) => {
                    const isDebit = entry.amount < 0;
                    const isAdmin = entry.reason.includes('ADMIN');
                    const isRefund = entry.reason.includes('REFUND');
                    const isTopup = entry.reason.includes('TOPUP') || entry.reason.includes('PLAN');

                    return (
                      <tr
                        key={entry.id}
                        className="hover:bg-surface-elevated/50 transition-colors"
                      >
                        {/* Transaction ID */}
                        <td className="py-3 px-4">
                          <button
                            type="button"
                            onClick={() => handleCopyId(entry.id)}
                            className="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-primary hover:underline focus:outline-none"
                            title="Click to copy transaction ID"
                          >
                            <span>{entry.id}</span>
                            {copiedId === entry.id ? (
                              <Check className="size-3 text-emerald-500" />
                            ) : (
                              <Copy className="size-3 text-foreground-muted" />
                            )}
                          </button>
                        </td>

                        {/* Workspace & User */}
                        <td className="py-3 px-4">
                          <div className="space-y-0.5">
                            <div className="font-semibold text-foreground">
                              {entry.workspaceName}
                            </div>
                            {entry.userEmail && (
                              <div className="text-[10px] text-foreground-muted font-mono truncate max-w-[160px]">
                                {entry.userEmail}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Type / Reason Badge */}
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                isAdmin
                                  ? 'bg-primary/10 text-primary border border-primary/20'
                                  : isRefund
                                    ? 'bg-cyan-500/10 text-cyan-500 border border-cyan-500/20'
                                    : isTopup
                                      ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                                      : 'bg-muted text-foreground-muted'
                              }`}
                            >
                              {isAdmin
                                ? 'Admin Adjustment'
                                : isRefund
                                  ? 'Refund'
                                  : isTopup
                                    ? 'Plan / Deposit'
                                    : 'AI Usage'}
                            </span>
                            <div className="text-[10px] text-foreground-muted truncate max-w-[200px]" title={entry.reason}>
                              {entry.reason}
                            </div>
                          </div>
                        </td>

                        {/* Credit Amount */}
                        <td className="py-3 px-4">
                          <span
                            className={`font-mono font-bold text-xs ${
                              isDebit ? 'text-rose-500' : 'text-emerald-500'
                            }`}
                          >
                            {isDebit ? '' : '+'}
                            {formatNumber(entry.amount)}
                          </span>
                        </td>

                        {/* Balance After */}
                        <td className="py-3 px-4 font-mono text-xs font-semibold text-foreground">
                          {formatNumber(entry.balanceAfter)}
                        </td>

                        {/* Reference & Model */}
                        <td className="py-3 px-4 text-foreground-muted text-[11px]">
                          <div className="space-y-0.5 font-mono text-[10px]">
                            {entry.modelCode && (
                              <div className="text-foreground font-semibold">
                                {entry.modelCode}
                              </div>
                            )}
                            {entry.referenceId && (
                              <div className="truncate max-w-[140px] text-foreground-muted">
                                {entry.referenceId}
                              </div>
                            )}
                            {entry.tokensUsed !== null && entry.tokensUsed !== undefined && (
                              <div>{entry.tokensUsed.toLocaleString()} tokens</div>
                            )}
                          </div>
                        </td>

                        {/* Timestamp */}
                        <td className="py-3 px-4 text-foreground-muted text-[11px]">
                          {new Date(entry.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manual Credit Adjustment Modal */}
      {selectedWallet && (
        <AdjustCreditsModal
          isOpen={isAdjustModalOpen}
          workspaceId={selectedWallet.workspaceId}
          workspaceName={selectedWallet.workspaceName}
          currentBalance={selectedWallet.balance}
          userEmail={selectedWallet.ownerEmail}
          onClose={() => {
            setIsAdjustModalOpen(false);
            setSelectedWallet(null);
          }}
          onSuccess={handleAdjustSuccess}
        />
      )}
    </div>
  );
};
